import type { SupabaseClient } from "@supabase/supabase-js";

export const SIGN_OUT_RELEASE_TIMEOUT_MS = 2000;

/**
 * Spec 04 §2.4: signing out releases the lease. end_dial_session ends the rep's active session and
 * releases every lease they hold. Best effort: never throws and never holds sign-out longer than
 * `timeoutMs` (the 10-minute lease expiry covers a lost request).
 */
export async function releaseDialerOnSignOut(
  client: SupabaseClient,
  salesId: number | string | null | undefined,
  timeoutMs = SIGN_OUT_RELEASE_TIMEOUT_MS,
): Promise<void> {
  if (salesId === null || salesId === undefined) return;
  const release = async () => {
    const { data } = await client
      .from("dial_sessions")
      .select("id")
      .eq("sales_id", Number(salesId))
      .is("ended_at", null)
      .maybeSingle();
    const id = (data as { id?: number } | null)?.id;
    if (id != null) await client.rpc("end_dial_session", { p_session_id: id });
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      release(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } catch {
    /* best effort */
  } finally {
    clearTimeout(timer);
  }
}

/** The sales id in the cached sale (authProvider); a missing or corrupted value is null, never a throw. */
export function cachedSalesId(raw: string | null | undefined): number | null {
  if (!raw) return null;
  try {
    const id = (JSON.parse(raw) as { id?: unknown } | null)?.id;
    return typeof id === "number" ? id : null;
  } catch {
    return null;
  }
}
